import { useCallback } from 'react';
import { Card, CardStatus } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H3, Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { Button, ButtonVariant, ButtonSize } from '@amzn/stencil-react-components/button';
import { Link } from '@amzn/stencil-react-components/link';
import { IconBinSmall, IconExternalLinkSmall } from '@amzn/stencil-react-icons';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

import { truncateStyle } from '../../../shared/ui/truncateStyle';
import { TAPPABLE_CARD_STYLE } from '../../../shared/ui/ListCard';
import { useMessages } from '../../../shared/i18n/useMessages';
import { ReviewQuestionStatus, type ReviewQuestion } from '../logic/reviewTypes';
import { buildPublishedQuestionHref } from '../logic/publishedQuestionHref';

/** New-tab link safety: `noopener` keeps the opened page off `window.opener`
 *  (reverse tabnabbing); `noreferrer` drops the referrer. Module-level so the
 *  attribute value is reference-stable. */
const NEW_TAB_REL = 'noopener noreferrer';

const PANEL_BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type PanelKey =
  | 'panelHeading'
  | 'panelCountSummary'
  | 'statusNeedsReview'
  | 'statusPublished'
  | 'questionNumberLabel'
  | 'deleteQuestionAltText'
  | 'openPublishedQuestionAltText';

/** Two-line ellipsis clamp for the card title. Module-level so the emotion
 *  css object is reference-stable across renders. */
const TITLE_CLAMP = truncateStyle(2);

/**
 * Selectable-draft card style. A plain `Card` rather than the shared
 * `ListCard`: that one is a real link (navigation), whereas selecting a draft
 * is an on-page action AND the card nests a delete button — Stencil's card
 * guidance says a card with interactive children must itself be a
 * non-interactive `Card`. The status border is shared so drafts sit
 * visually beside the audit/task list cards.
 *
 * TODO(audit-whiz): the selection is still a div `onClick`, so it is not
 * keyboard-operable. Making the body itself the control (rather than the
 * card) is the accessible shape; tracked as a follow-up so this CR stays a
 * no-behaviour-change refactor for this panel.
 */
const SELECTABLE_CARD_STYLE = {
  ...TAPPABLE_CARD_STYLE('color.status.neutral-fill'),
  cursor: 'pointer',
} as const;

/** Selected variant: the selectable card plus a muted background and an accent
 *  (primary-colored) left border, so the chosen draft is clearly highlighted —
 *  not just its bold title. Reuses the status left-border from
 *  {@link TAPPABLE_CARD_STYLE} (its width is a shared, already-sanctioned
 *  constant), recoloured to the info-fill accent, and adds the muted surface.
 *  Applied only to the currently-selected needs-review card. */
const SELECTED_CARD_STYLE = {
  ...TAPPABLE_CARD_STYLE('color.status.info-fill'),
  cursor: 'pointer',
  backgroundColor: token('color.surface.bg-muted') ?? '',
} as const;

/** Astra review status → Stencil BadgeType. Local to this feature — the
 *  shared ListCard status maps only cover audit/task statuses. */
const STATUS_BADGE_TYPE: Record<ReviewQuestionStatus, BadgeType> = {
  [ReviewQuestionStatus.NEEDS_REVIEW]: BadgeType.Neutral,
  [ReviewQuestionStatus.PUBLISHED]: BadgeType.Positive,
};

export interface AstraQuestionsPanelProps {
  readonly questions: readonly ReviewQuestion[];
  /** Currently-selected question id. */
  readonly selectedId: string;
  /** Called when a (non-published) question card is selected. */
  readonly onSelect: (id: string) => void;
  /** Called when an unpublished draft's delete control is used. */
  readonly onDelete: (id: string) => void;
  /** Author's program id (from the logged-in user), used to deep-link a
   *  published question to its detail page. Absent → published cards show the
   *  Question ID as plain text instead of a link. */
  readonly programId?: string | undefined;
  readonly dataTestId?: string;
}

interface QuestionCardProps {
  readonly question: ReviewQuestion;
  readonly index: number;
  readonly isSelected: boolean;
  readonly statusLabel: string;
  readonly numberLabel: string;
  readonly deleteAltText: string;
  /** Detail-page href for a published question, or undefined when it can't be
   *  built (no program id / not published) — then the id renders as plain text. */
  readonly questionHref?: string | undefined;
  /** Accessible label for the published-question link. */
  readonly openLinkAltText: string;
  readonly onSelect: (id: string) => void;
  readonly onDelete: (id: string) => void;
  readonly dataTestId?: string;
}

/** Body shared by both card variants: number label, title, status badge.
 *  Bolds the title when the card is the selected one (visible selection cue,
 *  since Card has no built-in selected style). */
/**
 * The id/number label at the top-left of a card. Early-return helper (not a
 * nested ternary in JSX) per the tsx-readability rule:
 *  - published WITH a resolvable href → a Link (Question ID) that opens the
 *    detail page in a new tab, with a trailing external-link icon;
 *  - published WITHOUT an href (no program id) → the Question ID as plain text;
 *  - needs-review → the "Question N" number label.
 */
function CardIdLabel({
  question,
  numberLabel,
  questionHref,
  openLinkAltText,
  testId,
}: {
  readonly question: ReviewQuestion;
  readonly numberLabel: string;
  readonly questionHref?: string | undefined;
  readonly openLinkAltText: string;
  readonly testId?: string;
}): JSX.Element {
  const isPublished = question.status === ReviewQuestionStatus.PUBLISHED;
  if (!isPublished || !question.questionId) {
    return (
      <Text fontSize="T100" color="color.text.secondary">
        {numberLabel}
      </Text>
    );
  }
  if (questionHref === undefined) {
    // Published but no deep-link could be built — show the id as plain text.
    return (
      <Text fontSize="T100" color="color.text.secondary">
        {question.questionId}
      </Text>
    );
  }
  return (
    <Row gridGap="dimensions.spacing.50" alignItems="center">
      <Link
        href={questionHref}
        target="_blank"
        rel={NEW_TAB_REL}
        aria-label={openLinkAltText}
        fontSize="T100"
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
      >
        {question.questionId}
      </Link>
      {/* standards-ignore: cross-feature — IconExternalLinkSmall is a generic
       *  new-tab affordance trailing the link; a shared wrapper over a bare
       *  icon would add indirection without value */}
      <IconExternalLinkSmall aria-hidden="true" />
    </Row>
  );
}

/** Body shared by both card variants: number/id label, title, status badge.
 *  Bolds the title when the card is the selected one (visible selection cue,
 *  since Card has no built-in selected style). */
function QuestionCardBody({
  question,
  numberLabel,
  statusLabel,
  isSelected,
  questionHref,
  openLinkAltText,
  idTestId,
  headerAction,
}: {
  readonly question: ReviewQuestion;
  readonly numberLabel: string;
  readonly statusLabel: string;
  readonly isSelected: boolean;
  readonly questionHref?: string | undefined;
  readonly openLinkAltText: string;
  readonly idTestId?: string;
  /** Optional trailing control on the header line (e.g. the delete button).
   *  Placed in the number-label row so it stays anchored to the top-right of
   *  the card regardless of how many lines the title wraps to. */
  readonly headerAction?: React.ReactNode;
}): JSX.Element {
  return (
    <Col
      gridGap="dimensions.spacing.100"
      flex={1}
      minWidth={0}
      // Selected draft: bold title (below) is the visual cue; aria-current
      // carries the selection state for assistive tech.
      aria-current={isSelected ? 'true' : undefined}
    >
      <Row alignItems="center" gridGap="dimensions.spacing.100">
        <CardIdLabel
          question={question}
          numberLabel={numberLabel}
          questionHref={questionHref}
          openLinkAltText={openLinkAltText}
          {...(idTestId !== undefined ? { testId: idTestId } : {})}
        />
        {/* Trailing action (delete) pinned to the right of the header line so
         *  it lines up across cards no matter the title's line count. */}
        {headerAction && (
          <>
            <Col flex={1} />
            {headerAction}
          </>
        )}
      </Row>
      <Text fontWeight={isSelected ? 'bold' : 'regular'} css={TITLE_CLAMP}>
        {question.title}
      </Text>
      <Row>
        <Badge type={STATUS_BADGE_TYPE[question.status]} value={statusLabel} />
      </Row>
    </Col>
  );
}

/** One question card. Published → non-clickable plain Card, no delete.
 *  Needs-review → selectable Card (status border) with a delete control. */
function QuestionCard({
  question,
  index,
  isSelected,
  statusLabel,
  numberLabel,
  deleteAltText,
  questionHref,
  openLinkAltText,
  onSelect,
  onDelete,
  dataTestId,
}: QuestionCardProps): JSX.Element {
  const cardTestId = dataTestId ? `${dataTestId}-card-${index}` : undefined;
  const idTestId = dataTestId ? `${dataTestId}-link-${index}` : undefined;

  const handleSelect = useCallback(() => onSelect(question.id), [onSelect, question.id]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      // Don't let the delete click also select the card.
      e.stopPropagation();
      onDelete(question.id);
    },
    [onDelete, question.id],
  );

  // Published: non-clickable, non-deletable. Needs review: selectable + deletable.
  const isPublished = question.status === ReviewQuestionStatus.PUBLISHED;

  // Delete control lives on the card header line (via headerAction) so it stays
  // top-right aligned across every card regardless of title wrap. Only rendered
  // for unpublished drafts.
  const deleteControl = isPublished ? undefined : (
    <Button
      variant={ButtonVariant.Tertiary}
      size={ButtonSize.Small}
      icon={<IconBinSmall aria-hidden="true" />}
      aria-label={deleteAltText}
      onClick={handleDelete}
      data-testid={dataTestId ? `${dataTestId}-delete-${index}` : undefined}
    />
  );

  return (
    <Card
      padding="dimensions.spacing.300"
      {...(cardTestId !== undefined ? { dataTestId: cardTestId } : {})}
      {...(isPublished
        ? { status: CardStatus.Success, statusIconAltText: statusLabel }
        : {
            css: isSelected ? SELECTED_CARD_STYLE : SELECTABLE_CARD_STYLE,
            onClick: handleSelect,
          })}
    >
      <QuestionCardBody
        question={question}
        numberLabel={numberLabel}
        statusLabel={statusLabel}
        isSelected={isSelected}
        questionHref={questionHref}
        openLinkAltText={openLinkAltText}
        {...(idTestId !== undefined ? { idTestId } : {})}
        headerAction={deleteControl}
      />
    </Card>
  );
}

/**
 * Right-side "Astra questions" panel for the Review & Publish screen.
 *
 * Renders a count summary + a vertical list of question cards. Needs-review
 * drafts are selectable and deletable; published questions render as
 * non-clickable cards (with their Q-ID + open-in-new-tab affordance) and
 * cannot be deleted from this view.
 *
 * Fully controlled — selection and deletion state live in the owning page/hook.
 */
export function AstraQuestionsPanel({
  questions,
  selectedId,
  onSelect,
  onDelete,
  programId,
  dataTestId,
}: AstraQuestionsPanelProps): JSX.Element {
  const { t, f } = useMessages<PanelKey>(PANEL_BUNDLE);

  const pendingCount = questions.filter(
    (q) => q.status === ReviewQuestionStatus.NEEDS_REVIEW,
  ).length;

  return (
    <Card
      padding="dimensions.spacing.300"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.200" width="100%">
        <H3>{t('panelHeading')}</H3>
        <Text fontSize="T100" color="color.text.secondary">
          {f('panelCountSummary', { total: questions.length, pending: pendingCount })}
        </Text>

        {questions.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            index={index}
            isSelected={question.id === selectedId}
            statusLabel={
              question.status === ReviewQuestionStatus.PUBLISHED
                ? t('statusPublished')
                : t('statusNeedsReview')
            }
            numberLabel={f('questionNumberLabel', { number: index + 1 })}
            deleteAltText={t('deleteQuestionAltText')}
            questionHref={buildPublishedQuestionHref({ question, programId })}
            openLinkAltText={f('openPublishedQuestionAltText', {
              id: question.questionId ?? '',
            })}
            onSelect={onSelect}
            onDelete={onDelete}
            {...(dataTestId ? { dataTestId } : {})}
          />
        ))}
      </Col>
    </Card>
  );
}
